import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { fmtDate, statusBadge } from "@/pages/admin/util";
import { Loader2, RefreshCw, ExternalLink } from "lucide-react";

const TYPES = ["", "GENERAL", "QUOTE_REVIEW", "INSURANCE_GUIDANCE", "MAINTENANCE_SUPPORT", "OTHER"];
const STATUSES = ["", "NEW", "REVIEWING", "AWAITING_INFO", "ADVISED", "CLOSED"];

export default function SupportCases() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (type) params.case_type = type;
      if (status) params.status = status;
      const { data } = await adminApi.get("/support-cases", { params });
      setItems(data.items);
    } finally { setLoading(false); }
  }, [type, status]);

  useEffect(() => { load(); }, [load]);

  // Deep-link from admin notification email: ?case_id=<uuid> goes to the workspace.
  useEffect(() => {
    const cid = searchParams.get("case_id");
    if (cid) {
      searchParams.delete("case_id");
      setSearchParams(searchParams, { replace: true });
      navigate(`/adminpanel/workspace/${cid}`);
    }
  }, [searchParams, setSearchParams, navigate]);

  return (
    <div data-testid="admin-support-cases-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-7">
        <div>
          <p className="text-[10.5px] tracking-[0.32em] uppercase text-[#B88A2A] font-semibold">All Cases</p>
          <h1 className="font-display text-[32px] lg:text-[40px] leading-[1.05] tracking-tight text-[#071A33] mt-2">Support Cases</h1>
          <p className="text-sm text-[#475569] mt-2 max-w-2xl">Every active enquiry, filterable by type and status.</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="field-input h-10" value={type} onChange={(e) => setType(e.target.value)} data-testid="cases-type-filter">
            {TYPES.map((t) => <option key={t} value={t}>{t || "All types"}</option>)}
          </select>
          <select className="field-input h-10" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cases-status-filter">
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
          <button onClick={load} className="btn-outline h-10 py-0 px-4" data-testid="cases-refresh"><RefreshCw size={14}/></button>
        </div>
      </div>

      <div className="bg-white border border-[#E2DCD0] rounded-xl overflow-x-auto shadow-[0_1px_0_rgba(7,26,51,0.04)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F7F4ED] text-left text-[#475569] text-xs uppercase tracking-[0.15em]">
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Park</th>
              <th className="px-4 py-3">Plot</th>
              <th className="px-4 py-3">Urgency</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={16}/></td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#475569]">No cases yet.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-[#E2DCD0] hover:bg-[#F7F4ED]/40">
                <td className="px-4 py-3 text-[#475569]">{c.case_type}</td>
                <td className="px-4 py-3 font-medium text-[#071A33]">{c.full_name}</td>
                <td className="px-4 py-3 text-[#475569]">{c.email}</td>
                <td className="px-4 py-3 text-[#475569]">{c.park_name || "—"}</td>
                <td className="px-4 py-3 text-[#475569]">{c.plot_number || "—"}</td>
                <td className="px-4 py-3 text-[#475569]">{c.urgency || "—"}</td>
                <td className="px-4 py-3">{statusBadge(c.status)}</td>
                <td className="px-4 py-3 text-[#475569]">{fmtDate(c.created_at, true)}</td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/adminpanel/workspace/${c.id}`} className="text-xs px-2.5 py-1.5 bg-[#0F766E] text-white hover:bg-[#0a5b55] inline-flex items-center gap-1 font-medium rounded" data-testid={`sc-view-${c.id}`}>
                    Open workspace <ExternalLink size={11}/>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
